import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Grid, Container, Stack, Divider, Chip, 
  useTheme, useMediaQuery, Fade, Grow, Zoom, createTheme, ThemeProvider, CssBaseline
} from '@mui/material';
import { 
  Speed, BarChart, Notifications, 
  Science, Timeline, Lock 
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0f0c29',
      paper: '#1a1a2e',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0f0c29 !important',
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
        },
        html: {
          backgroundColor: '#0f0c29 !important',
        },
        '#root': {
          backgroundColor: '#0f0c29 !important',
        }
      },
    },
  },
});

// Binance Logo SVG Component
const BinanceLogo = ({ width = 140 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    {/* Binance Diamond Icon */}
    <svg viewBox="0 0 20 20" style={{ width: 28, height: 28 }} fill="#F0B90B">
      <path d="M10 0l2.5 2.5L10 5 7.5 2.5 10 0z"/>
      <path d="M15 5l2.5 2.5L15 10l-2.5-2.5L15 5z"/>
      <path d="M5 5l2.5 2.5L5 10 2.5 7.5 5 5z"/>
      <path d="M10 10l2.5 2.5L10 15l-2.5-2.5L10 10z"/>
      <path d="M5 15l2.5 2.5L5 20l-2.5-2.5L5 15z"/>
      <path d="M15 15l2.5 2.5L15 20l-2.5-2.5L15 15z"/>
      <path d="M10 10l2.5-2.5L15 10l-2.5 2.5L10 10z"/>
      <path d="M10 10l-2.5-2.5L5 10l2.5 2.5L10 10z"/>
    </svg>
    {/* Binance Text */}
    <Typography 
      sx={{ 
        fontSize: width / 5, 
        fontWeight: 700, 
        color: '#F0B90B !important',
        letterSpacing: 1
      }}
    >
      BINANCE
    </Typography>
  </Box>
);

// Discord Icon SVG Component
const DiscordIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// Generic Cryptocurrency Animation Component
const CryptoAnimation = () => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  return (
    <svg viewBox="0 0 500 500" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <linearGradient id="cryptoGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00d2ff" />
          <stop offset="100%" stopColor="#3a7bd5" />
        </linearGradient>
        <linearGradient id="cryptoGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3a7bd5" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
        <linearGradient id="glowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00d2ff" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#3a7bd5" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="chartLine" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00d2ff" />
          <stop offset="50%" stopColor="#00e5ff" />
          <stop offset="100%" stopColor="#3a7bd5" />
        </linearGradient>
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f7931a" />
          <stop offset="100%" stopColor="#ffab40" />
        </linearGradient>
        <linearGradient id="ethGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#627eea" />
          <stop offset="100%" stopColor="#8c9eff" />
        </linearGradient>
        
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="bigGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="20" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      <circle cx="250" cy="250" r="180" fill="url(#glowGradient)" filter="url(#bigGlow)" opacity="0.5">
        <animate attributeName="r" values="170;190;170" dur="4s" repeatCount="indefinite"/>
      </circle>
      
      <g style={{ transformOrigin: '250px 250px' }}>
        <ellipse cx="250" cy="250" rx="200" ry="70" fill="none" stroke="url(#cryptoGradient1)" strokeWidth="1" opacity="0.3" strokeDasharray="10,5">
          <animateTransform attributeName="transform" type="rotate" from="0 250 250" to="360 250 250" dur="30s" repeatCount="indefinite"/>
        </ellipse>
        <ellipse cx="250" cy="250" rx="160" ry="55" fill="none" stroke="url(#cryptoGradient2)" strokeWidth="1.5" opacity="0.4">
          <animateTransform attributeName="transform" type="rotate" from="30 250 250" to="390 250 250" dur="25s" repeatCount="indefinite"/>
        </ellipse>
        <ellipse cx="250" cy="250" rx="120" ry="40" fill="none" stroke="#00d2ff" strokeWidth="2" opacity="0.5">
          <animateTransform attributeName="transform" type="rotate" from="60 250 250" to="420 250 250" dur="20s" repeatCount="indefinite"/>
        </ellipse>
      </g>
      
      <g filter="url(#glow)">
        <circle cx="250" cy="250" r="65" fill="#1a1a2e" stroke="url(#goldGradient)" strokeWidth="3">
          <animate attributeName="r" values="63;67;63" dur="3s" repeatCount="indefinite"/>
        </circle>
        <text x="250" y="270" textAnchor="middle" fontSize="60" fontWeight="bold" fill="url(#goldGradient)" fontFamily="system-ui">₿</text>
      </g>
      
      <g>
        <animateTransform attributeName="transform" type="rotate" from="0 250 250" to="360 250 250" dur="12s" repeatCount="indefinite"/>
        <g transform="translate(250, 250)">
          <g transform="translate(160, 0)">
            <circle r="28" fill="#1a1a2e" stroke="url(#ethGradient)" strokeWidth="2" filter="url(#glow)"/>
            <text y="8" textAnchor="middle" fontSize="28" fill="url(#ethGradient)" fontFamily="system-ui">Ξ</text>
          </g>
        </g>
      </g>
      
      <g>
        <animateTransform attributeName="transform" type="rotate" from="120 250 250" to="480 250 250" dur="15s" repeatCount="indefinite"/>
        <g transform="translate(250, 250)">
          <g transform="translate(130, 0)">
            <circle r="22" fill="#1a1a2e" stroke="#26a17b" strokeWidth="2" filter="url(#glow)"/>
            <text y="7" textAnchor="middle" fontSize="18" fill="#26a17b" fontWeight="bold" fontFamily="system-ui">$</text>
          </g>
        </g>
      </g>
      
      <g>
        <animateTransform attributeName="transform" type="rotate" from="240 250 250" to="600 250 250" dur="18s" repeatCount="indefinite"/>
        <g transform="translate(250, 250)">
          <g transform="translate(185, 0)">
            <circle r="18" fill="#1a1a2e" stroke="#00d2ff" strokeWidth="2" filter="url(#glow)"/>
            <text y="6" textAnchor="middle" fontSize="14" fill="#00d2ff" fontWeight="bold" fontFamily="system-ui">◈</text>
          </g>
        </g>
      </g>
      
      <g opacity="0.8">
        <path d="M50,380 Q100,370 130,350 T200,300 T270,320 T340,260 T400,220 T450,180" fill="none" stroke="url(#chartLine)" strokeWidth="3" strokeLinecap="round" filter="url(#glow)" strokeDasharray="500" strokeDashoffset={mounted ? "0" : "500"} style={{ transition: 'stroke-dashoffset 2s ease-out' }}/>
        <polygon points="450,180 440,195 455,190" fill="#00d2ff" opacity={mounted ? 1 : 0} style={{ transition: 'opacity 0.5s ease-out 1.8s' }}>
          <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite"/>
        </polygon>
      </g>
      
      {[{ x: 130, y: 350, delay: 0 }, { x: 200, y: 300, delay: 0.3 }, { x: 270, y: 320, delay: 0.6 }, { x: 340, y: 260, delay: 0.9 }, { x: 400, y: 220, delay: 1.2 }].map((point, i) => (
        <g key={i}>
          <circle cx={point.x} cy={point.y} r="5" fill="#00d2ff" opacity={mounted ? 1 : 0} style={{ transition: `opacity 0.3s ease-out ${point.delay + 0.5}s` }}>
            <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" begin={`${point.delay}s`}/>
          </circle>
        </g>
      ))}
      
      <g opacity={mounted ? 1 : 0} style={{ transition: 'opacity 0.5s ease-out 1.5s' }}>
        <rect x="360" y="140" width="70" height="28" rx="6" fill="rgba(0, 210, 255, 0.15)" stroke="rgba(0, 210, 255, 0.3)" strokeWidth="1"/>
        <text x="395" y="160" textAnchor="middle" fontSize="14" fill="#00d2ff" fontWeight="600" fontFamily="system-ui">+24.5%</text>
      </g>
      
      {[{ x: 70, y: 180 }, { x: 430, y: 280 }, { x: 380, y: 420 }, { x: 120, y: 400 }].map((node, i) => (
        <g key={i}>
          <line x1={node.x} y1={node.y} x2="250" y2="250" stroke="#3a7bd5" strokeWidth="1" opacity="0.2" strokeDasharray="4,4">
            <animate attributeName="opacity" values="0.1;0.3;0.1" dur={`${2 + i * 0.3}s`} repeatCount="indefinite"/>
          </line>
          <circle cx={node.x} cy={node.y} r="4" fill="#3a7bd5" opacity="0.6">
            <animate attributeName="r" values="3;5;3" dur={`${2 + i * 0.3}s`} repeatCount="indefinite"/>
          </circle>
        </g>
      ))}
    </svg>
  );
};

// Styles object with !important flags
const styles = {
  darkBg: {
    background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
    backgroundColor: '#0f0c29 !important',
  },
  solidDarkBg: {
    backgroundColor: '#0f0c29 !important',
  }
};

function LandingPageContent() {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <Speed sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "Ultra-Low Latency",
      desc: "Lightning-fast trade execution with minimal latency to ensure your orders are placed at the best possible prices"
    },
    {
      icon: <Timeline sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "High Frequency Trading",
      desc: "Execute trades on tick-level data for maximum precision and speed in volatile market conditions",
    },
    {
      icon: <Lock sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "Secure & Encrypted",
      desc: "Your data is encrypted and securely stored. API keys are protected with industry-standard encryption"
    },
    {
      icon: <Notifications sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "TradingView Alerts",
      desc: "Connect your TradingView alerts to automatically trigger bot trades based on your custom indicators"
    },
    {
      icon: <BarChart sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "Advanced Analytics",
      desc: "Comprehensive backtesting with historical data to validate your strategies"
    },
    {
      icon: <Science sx={{ fontSize: 40, color: '#00d2ff' }} />,
      title: "Paper Trading",
      desc: "Test your strategies risk-free with simulated trading before going live"
    },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
        backgroundColor: '#0f0c29 !important',
        color: 'white',
        fontFamily: '"Inter", sans-serif',
        overflowX: 'hidden',
        '& *': {
          boxSizing: 'border-box',
        }
      }}
    >
      {/* App Bar / Header */}
      <Box sx={{
        py: 3,
        px: { xs: 2, md: 6 },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(10, 10, 40, 0.7) !important'
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 700,
          background: 'linear-gradient(90deg, #00d2ff 0%, #3a7bd5 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          CRYPTOLOGICS-AI
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => history.push('/sign-in')}
          sx={{
            fontWeight: 600,
            color: 'white !important',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'white'
            }
          }}
        >
          Sign In
        </Button>
      </Box>

      {/* Hero Section */}
      <Box 
        id="hero"
        sx={{ 
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
          backgroundColor: '#0f0c29 !important'
        }}
      >
        <Container maxWidth="lg" sx={{ py: 10, position: 'relative' }}>
          <Box sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 600,
            height: 600,
            background: 'radial-gradient(circle, rgba(58, 123, 213, 0.15) 0%, transparent 70%)',
            zIndex: 0
          }} />
          
          <Grid container spacing={6} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={800}>
                <Box>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Chip label="FREE TO USE" sx={{ 
                      fontWeight: 600,
                      bgcolor: 'rgba(46, 204, 113, 0.2) !important',
                      color: '#2ecc71 !important',
                      fontSize: '0.8rem',
                      height: 28
                    }} />
                    <Chip label="BETA" sx={{ 
                      fontWeight: 600,
                      bgcolor: 'rgba(0, 210, 255, 0.2) !important',
                      color: '#00d2ff !important',
                      fontSize: '0.8rem',
                      height: 28
                    }} />
                  </Stack>
                  
                  <Typography variant="h3" component="h1" sx={{
                    fontWeight: 800,
                    mb: 3,
                    lineHeight: 1.2,
                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                    background: 'linear-gradient(90deg, #ffffff 0%, #a1c4fd 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Institutional-Grade Crypto Trading Automation
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    mb: 4,
                    fontSize: '1.1rem',
                    opacity: 0.9,
                    lineHeight: 1.7,
                    color: 'white !important'
                  }}>
                    Harness the power of ultra low latency platform to execute sophisticated trading strategies 24/7 on Binance. 
                    Our platform combines institutional-grade technology with an intuitive interface — completely free.
                  </Typography>
                  
                  <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => history.push('/sign-up')}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: '1rem',
                        background: 'linear-gradient(90deg, #00d2ff 0%, #3a7bd5 100%) !important',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(58, 123, 213, 0.5)'
                        }
                      }}
                    >
                      Sign Up for Free
                    </Button>
                  </Stack>
                  
                  <Stack direction="row" spacing={2} sx={{ opacity: 0.8 }}>
                    <Typography variant="caption" sx={{ color: 'white !important' }}>100% Free</Typography>
                    <Typography variant="caption" sx={{ color: 'white !important' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: 'white !important' }}>No Credit Card Required</Typography>
                    <Typography variant="caption" sx={{ color: 'white !important' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: 'white !important' }}>99.9% Uptime</Typography>
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000}>
                <Box sx={{ position: 'relative', p: 2 }}>
                  <CryptoAnimation />
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Supported Exchange */}
      <Box 
        id="exchange"
        sx={{ 
          py: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
          backgroundColor: '#0f0c29 !important'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" sx={{ mb: 3, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 2, color: 'white !important' }}>
            Supported Exchange
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: 0.8, '&:hover': { opacity: 1 } }}>
            <BinanceLogo width={160} />
          </Box>
        </Container>
      </Box>
      
      {/* Features Section */}
      <Box 
        id="features"
        sx={{ 
          py: 10, 
          position: 'relative',
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
          backgroundColor: '#0f0c29 !important'
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 30% 50%, rgba(58, 123, 213, 0.1) 0%, transparent 50%)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" component="h3" align="center" sx={{ 
            fontWeight: 700,
            mb: 2,
            fontSize: isMobile ? '2rem' : '2.5rem',
            color: 'white !important'
          }}>
            Professional Trading Tools
          </Typography>
          
          <Typography variant="body1" align="center" sx={{ 
            mb: 6,
            maxWidth: 700,
            mx: 'auto',
            opacity: 0.8,
            fontSize: '1.1rem',
            color: 'white !important'
          }}>
            Everything you need to trade like an institution, without the complexity — and it's free
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={(index + 1) * 300}>
                  <Box sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(15, 12, 41, 0.8) !important',
                    backgroundColor: 'rgba(15, 12, 41, 0.8) !important',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.2)',
                      borderColor: 'rgba(0, 210, 255, 0.5)',
                      background: 'rgba(26, 26, 46, 0.9) !important'
                    }
                  }}>
                    {feature.comingSoon && (
                      <Chip 
                        label="COMING SOON" 
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 22,
                          bgcolor: 'rgba(255, 193, 7, 0.2) !important',
                          color: '#ffc107 !important',
                          border: '1px solid rgba(255, 193, 7, 0.3)'
                        }} 
                      />
                    )}
                    
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h4" sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      opacity: feature.comingSoon ? 0.7 : 1,
                      color: 'white !important'
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      opacity: feature.comingSoon ? 0.5 : 0.8,
                      lineHeight: 1.7,
                      color: 'white !important'
                    }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Stats Section */}
      <Box 
        id="stats"
        sx={{ 
          py: 8,
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
          backgroundColor: '#0f0c29 !important'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} justifyContent="center">
            {[
              { value: '24/7', label: 'Market Monitoring' },
              { value: 'Binance', label: 'Supported Exchange' },
              { value: '99.9%', label: 'Uptime' },
              { value: 'Free', label: '' }
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box textAlign="center">
                  <Typography variant="h3" sx={{ 
                    fontWeight: 800,
                    mb: 1,
                    background: 'linear-gradient(90deg, #00d2ff 0%, #3a7bd5 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: index === 1 ? '1.8rem' : '2.5rem'
                  }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.8, color: 'white !important' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Box 
        id="cta"
        sx={{ 
          py: 12, 
          background: 'linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #16213e 100%) !important',
          backgroundColor: '#0f0c29 !important'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(0, 210, 255, 0.1) 0%, rgba(58, 123, 213, 0.2) 100%) !important',
            textAlign: 'center',
            border: '1px solid rgba(0, 210, 255, 0.3)',
            boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.3)'
          }}>
            <Typography variant="h4" component="h3" sx={{ 
              fontWeight: 700,
              mb: 3,
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: 'white !important'
            }}>
              Ready to Transform Your Trading?
            </Typography>
            
            <Typography variant="body1" sx={{ 
              mb: 5,
              mx: 'auto',
              opacity: 0.9,
              maxWidth: 600,
              lineHeight: 1.7,
              color: 'white !important'
            }}>
              Join traders using Cryptologics-AI to automate their strategies on Binance — completely free.
            </Typography>
            
            <Button 
              variant="contained" 
              size="large"
              onClick={() => history.push('/sign-up')}
              sx={{
                px: 6,
                py: 2,
                fontWeight: 600,
                fontSize: '1rem',
                background: 'linear-gradient(90deg, #00d2ff 0%, #3a7bd5 100%) !important',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 25px rgba(58, 123, 213, 0.5)'
                }
              }}
            >
              Sign Up for Free
            </Button>
            
            <Typography variant="caption" display="block" sx={{ mt: 3, opacity: 0.7, color: 'white !important' }}>
              No credit card required • Free
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Footer */}
      <Box sx={{ 
        py: 6,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        background: '#0a0818 !important',
        backgroundColor: '#0a0818 !important'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} justifyContent="space-between">
            <Grid item xs={12} md={5}>
              <Typography variant="h6" component="div" sx={{ mb: 3, fontWeight: 700, color: 'white !important' }}>
                CRYPTOLOGICS-AI
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 3, lineHeight: 1.7, color: 'white !important' }}>
                Institutional-grade crypto trading automation powered by artificial intelligence. Free to use.
              </Typography>
              <Button
                variant="outlined"
                component="a"
                href="https://discord.gg/b5x2HgFsS"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DiscordIcon size={20} />}
                sx={{
                  color: '#ffffff !important',
                  borderColor: 'rgba(88, 101, 242, 0.5)',
                  backgroundColor: 'rgba(88, 101, 242, 0.1)',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2.5,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(88, 101, 242, 0.25)',
                    borderColor: '#5865F2',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Join our Discord
              </Button>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" component="div" sx={{ mb: 3, fontWeight: 600, color: 'white !important' }}>
                Quick Links
              </Typography>
              <Stack spacing={2}>
                {[
                  { label: 'Features', id: 'features' },
                  { label: 'Supported Exchange', id: 'exchange' },
                  { label: 'Stats', id: 'stats' },
                  { label: 'Get Started', id: 'cta' }
                ].map((item) => (
                  <Typography 
                    key={item.id} 
                    variant="body2" 
                    onClick={() => scrollToSection(item.id)}
                    sx={{ 
                      opacity: 0.7,
                      color: 'white !important',
                      cursor: 'pointer',
                      '&:hover': { opacity: 1, color: '#00d2ff !important' },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 6, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6, color: 'white !important' }}>
              © {new Date().getFullYear()} Cryptologics-AI. All rights reserved.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.6, mt: { xs: 2, md: 0 }, color: 'white !important' }}>
              Free to use • No hidden fees
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <LandingPageContent />
    </ThemeProvider>
  );
}